import { PACIFIC_TIME_ZONE } from './formatInstantInTimeZone';

/**
 * Format an instant in Pacific time as `Mon D, YYYY, H:MM AM/PM ZZZ` — e.g.
 * `Jun 1, 2026, 1:30 PM PDT`.
 *
 * Pinned to Pacific rather than the reader's own zone because the audience is
 * SF-based operators comparing a timestamp against a deploy log, a Slack
 * message, or each other. `Intl` resolves PST vs PDT from the instant, so the
 * abbreviation is always the one that was in effect.
 *
 * Uses explicit component options rather than `dateStyle` / `timeStyle`,
 * which the spec forbids combining with `timeZoneName`.
 *
 * An unparseable input surfaces as itself rather than as a formatted date: a
 * string comes back verbatim, and an invalid `Date` — which has no original
 * text to return — comes back as `Invalid Date`.
 */
export default function formatPacificDateTime(instant: string | Date): string {
  const date = instant instanceof Date ? instant : new Date(instant);
  if (Number.isNaN(date.getTime())) {
    return typeof instant === 'string' ? instant : String(instant);
  }
  return new Intl.DateTimeFormat('en-US', {
    timeZone: PACIFIC_TIME_ZONE,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(date);
}
